'use client';

import { useEffect, useRef } from 'react';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';

type AnalyticsHook = ReturnType<typeof useAnalytics>;

/**
 * Extracts only the keys whose values are callable functions from the analytics hook.
 * This filters out non-function properties inherited from AnalyticsInstance.
 */
type TrackingMethodName = {
  [K in keyof AnalyticsHook]: AnalyticsHook[K] extends (...args: any[]) => void
    ? K
    : never;
}[keyof AnalyticsHook];

interface AnalyticsPageTrackerProps<K extends TrackingMethodName> {
  event: K;
  payload: AnalyticsHook[K] extends (...args: infer P) => void ? P : never;
}

/**
 * Client Component that fires an analytics event on mount.
 * Designed to be used inside Server Components (async pages) where hooks cannot be called directly.
 *
 * The event fires exactly once per mount, once analytics is ready. Firing on
 * tracking-function identity instead would emit the event twice whenever the
 * provider rebuilds its instance, and the second fire would read the first as
 * its own `previousEvent`.
 *
 * @example
 * // In a Server Component page:
 * <AnalyticsPageTracker event={'viewedProfileEvent'} payload={[provider]} />
 */
export const AnalyticsPageTracker = <K extends TrackingMethodName>({
  event,
  payload,
}: AnalyticsPageTrackerProps<K>): null => {
  const analytics = useAnalytics();
  const hasFiredRef = useRef<boolean>(false);

  const fn = analytics[event];
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  const { isReady } = analytics;

  useEffect(() => {
    if (!isReady || hasFiredRef.current) {
      return;
    }
    if (typeof fnRef.current === 'function') {
      hasFiredRef.current = true;
      (fnRef.current as (...args: unknown[]) => void)(...payloadRef.current);
    }
  }, [isReady]);

  return null;
};
